import { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Loader2, Sparkles, Coins, Download, Copy, Check } from 'lucide-react';
import { generateReport } from '../services/gradingService';
import { saveJob } from '../services/jobsService';
import { useAuth } from '../contexts/AuthContext';
import { markdownToWordBlob, titleToFilename } from '../utils/docx';
import type { GenerateParams, GeneratedReport } from '../types';

const toneOptions: { value: GenerateParams['tone']; label: string }[] = [
  { value: 'academic', label: 'Academic' },
  { value: 'business', label: 'Business' },
  { value: 'technical', label: 'Technical' },
  { value: 'casual', label: 'Casual' },
];

const defaultSections = ['Introduction', 'Background', 'Analysis', 'Recommendations', 'Conclusion'];

export default function Generate() {
  const { user, addCredits } = useAuth();
  const [form, setForm] = useState<GenerateParams>({
    topic: '',
    audience: 'University lecturer',
    lengthWords: 1200,
    tone: 'academic',
    sections: defaultSections,
    citations: true,
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<GeneratedReport | null>(null);
  const [copied, setCopied] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.topic.trim()) return toast.error('Please enter a topic');
    if (!user || user.credits < 1) return toast.error('You need at least 1 credit to generate.');
    setLoading(true);
    try {
      const r = await generateReport(form);
      setResult(r);
      addCredits(-1);
      // Persist the full markdown so the user can re-download from History.
      // Retention is plan-based (7 d free/try/starter, 30 d pro/team) and
      // enforced by the Firestore TTL policy on the jobs collection, reading
      // `expiresAt` stamped inside saveJob. We pass the current plan so the
      // service can compute the right window without reaching into auth state.
      await saveJob(
        {
          kind: 'generation',
          title: r.title,
          words: r.wordCount,
          markdown: r.markdown,
        },
        user.plan,
      );
      toast.success('Report generated');
    } catch (e: any) {
      toast.error(e.message ?? 'Generation failed');
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    if (!result) return;
    await navigator.clipboard.writeText(result.markdown);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const download = (kind: 'md' | 'txt' | 'doc') => {
    if (!result) return;
    const blob =
      kind === 'doc'
        ? markdownToWordBlob(result.markdown, result.title)
        : new Blob([result.markdown], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${titleToFilename(result.title)}.${kind}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleSection = (s: string) => {
    setForm((f) => {
      const cur = f.sections ?? [];
      return { ...f, sections: cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s] };
    });
  };

  return (
    <div className="container-wide py-10">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-display font-bold">Generate a report</h1>
          <p className="text-ink-600 mt-1">Give us a topic, audience, and length — we draft it.</p>
        </div>
        {user && (
          <span className="chip bg-brand-50 text-brand-700 border border-brand-100 px-3 py-1.5 text-sm">
            <Coins className="w-4 h-4 mr-1.5" /> {user.credits} credits
          </span>
        )}
      </div>

      <div className="mt-6 grid lg:grid-cols-5 gap-6">
        <form onSubmit={submit} className="lg:col-span-2 card p-6 space-y-4">
          <div>
            <label className="label">Topic</label>
            <textarea className="input min-h-[88px]" value={form.topic}
              onChange={(e) => setForm({ ...form, topic: e.target.value })}
              placeholder="e.g. Impact of large language models on medical education" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Audience</label>
              <input className="input" value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} />
            </div>
            <div>
              <label className="label">Length (words)</label>
              <input type="number" min={300} max={5000} step={100} className="input"
                value={form.lengthWords} onChange={(e) => setForm({ ...form, lengthWords: Number(e.target.value) })} />
            </div>
          </div>
          <div>
            <label className="label">Tone</label>
            <div className="grid grid-cols-4 gap-2">
              {toneOptions.map((t) => (
                <button type="button" key={t.value}
                  onClick={() => setForm({ ...form, tone: t.value })}
                  className={`px-3 py-2 text-sm rounded-lg border transition-colors ${form.tone === t.value
                    ? 'border-brand-500 bg-brand-50 text-brand-700'
                    : 'border-ink-200 text-ink-700 hover:bg-ink-50'}`}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="label">Sections</label>
            <div className="flex flex-wrap gap-1.5">
              {defaultSections.map((s) => {
                const active = form.sections?.includes(s);
                return (
                  <button type="button" key={s} onClick={() => toggleSection(s)}
                    className={`chip text-sm border ${active ? 'bg-brand-50 text-brand-700 border-brand-200' : 'bg-white border-ink-200 text-ink-600'}`}>
                    {s}
                  </button>
                );
              })}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-ink-700">
            <input type="checkbox" checked={form.citations} onChange={(e) => setForm({ ...form, citations: e.target.checked })} />
            Include inline citations
          </label>

          <button type="submit" disabled={loading} className="btn-primary w-full">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Drafting…</> : <><Sparkles className="w-4 h-4" /> Generate (1 credit)</>}
          </button>
          {user && user.credits < 1 && (
            <p className="text-xs text-amber-700">
              Out of credits — <Link to="/pricing" className="underline">top up</Link>.
            </p>
          )}
        </form>

        <div className="lg:col-span-3">
          {!result && !loading && (
            <div className="card p-10 text-center text-ink-500 h-full grid place-items-center">
              <div>
                <Sparkles className="w-10 h-10 mx-auto text-brand-400" />
                <p className="mt-3">Your generated report will appear here.</p>
              </div>
            </div>
          )}
          {loading && (
            <div className="card p-10 text-center">
              <Loader2 className="w-8 h-8 text-brand-600 animate-spin mx-auto" />
              <p className="mt-3 text-sm text-ink-600">Drafting, structuring, and polishing your report…</p>
            </div>
          )}
          {result && (
            <div className="card p-6 animate-fade-in">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <h2 className="font-display font-bold text-xl">{result.title}</h2>
                  <p className="text-xs text-ink-500">{result.wordCount} words</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={copy} className="btn-secondary btn-sm">
                    {copied ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy</>}
                  </button>
                  <button onClick={() => download('md')} className="btn-secondary btn-sm">
                    <Download className="w-4 h-4" /> .md
                  </button>
                  <button onClick={() => download('txt')} className="btn-secondary btn-sm">
                    <Download className="w-4 h-4" /> .txt
                  </button>
                  <button onClick={() => download('doc')} className="btn-primary btn-sm">
                    <Download className="w-4 h-4" /> Word
                  </button>
                </div>
              </div>
              <div className="mt-5 p-5 bg-ink-50 rounded-xl max-h-[560px] overflow-auto">
                <pre className="whitespace-pre-wrap font-sans text-sm text-ink-800 leading-relaxed">{result.markdown}</pre>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

